import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, mergeProps as _mergeProps, toDisplayString as _toDisplayString, openBlock as _openBlock, createBlock as _createBlock } from "vue"

import CurrentLanguage from '@/components/CurrentLanguage.vue';
import { computed, markRaw, Ref, ref, watch } from 'vue';
import CategoryField from '../categories/CategoryField.vue';
import { Category } from '@/models/category.model';
import { EventItem } from '@/models/event-item.model';
import { Story } from '@/models/story.model';
import { LearningItem } from '@/models/learning-item.model';
import initiativesService from '@/services/initiatives.service';
import learningItemService from '@/services/learning-item.service';
import eventsService from '@/services/events.service';
import storiesService from '@/services/stories.service';
import { Initiative } from '@/models/initiative.model';
import insightService from '@/services/insight.service';
import { useStore } from 'vuex';
import { Actions, Mutations } from '@/store';
import { Location } from '@/models/location.model';
import LocationField from '../locations/LocationField.vue';
import { TYPE_INITIATIVE } from '@/util/constants';
import EventItemForm from '../events/EventItemForm.vue';
import LearningItemForm from '../learning/LearningItemForm.vue';
import ContentItemForm from './ContentItemForm.vue';
import StoryForm from '../stories/StoryForm.vue';
import { FilterData } from '@/services/users.service.js';

interface ResultItem {
  type: string;
  title: string;
  category: Category;
  item: Initiative | LearningItem | EventItem | Story;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'InsightList',
  setup(__props) {

const headers = [
  { title: 'ID', key: 'item.id', width: '5%' },
  { title: 'Type', key: 'type', width: '10%' },
  { title: 'Title', key: 'title', width: '40%' },
  { title: 'Category', key: 'category', width: '25%' },
];

const store = useStore();
const filteredContent = ref<ResultItem[]>([]);

const filterData = ref<FilterData>({
  search: '',
  level: null,
  page: 1,
  count: 25,
  sort: 'id',
  direction: 'asc',
});

const category = ref<Category>();
const location = ref<Location>();

const initiativesCount = ref<number>(0);
const learningItemsCount = ref<number>(0);
const eventItemsCount = ref<number>(0);
const storiesCount = ref<number>(0);

const total = computed<number>(
  () =>
    initiativesCount.value +
    learningItemsCount.value +
    eventItemsCount.value +
    storiesCount.value,
);

const search = async () => {
  if (category.value || location.value) {
    const initiatives = await initiativesService.search(
      '',
      filterData.value.page,
      category.value?.id,
      location.value?.id,
    );
    const learningItems = location.value
      ? { total: 0, items: [] }
      : await learningItemService.search(
          '',
          filterData.value.page,
          category.value?.id,
        );
    const eventItems = await eventsService.search(
      '',
      filterData.value.page,
      category.value?.id,
      location.value?.id,
    );
    const stories = location.value
      ? { total: 0, items: [] }
      : await storiesService.search(
          '',
          filterData.value.page,
          category.value?.id,
        );

    initiativesCount.value = initiatives.total;
    learningItemsCount.value = learningItems.total;
    eventItemsCount.value = eventItems.total;
    storiesCount.value = stories.total;

    filteredContent.value = [
      ...initiatives.items.map((i) => ({
        item: i,
        title: i.title,
        type: 'Initiative',
        category: i.mainCategory!,
      })),
      ...learningItems.items.map((i) => ({
        item: i,
        title: i.title,
        type: 'Learning Item',
        category: i.mainCategory!,
      })),
      ...stories.items.map((i) => ({
        item: i,
        title: i.title,
        type: 'Quiz',
        category: i.mainCategory!,
      })),
      ...eventItems.items.map((i) => ({
        item: i,
        title: i.title,
        type: 'Event',
        category: i.mainCategory!,
      })),
    ].sort((a, b) => (a.title > b.title ? 1 : -1));
  }
};

watch(() => category.value, search);
watch(() => location.value, search);

function updateCategories(isActive: Ref) {
  store.dispatch(Actions.ShowNotification, {
    message: 'Updating categories',
    type: 'info',
  });

  isActive.value = false;
  insightService.updateCategories();
}

function updateLocations() {
  store.dispatch(Actions.ShowNotification, {
    message: 'Updating locations',
    type: 'info',
  });

  insightService.updateLocations();
}

async function editStory(s: Story) {
  const story = await storiesService.get(s.id);

  store.commit(Mutations.ShowDrawer, {
    component: markRaw(StoryForm),
    data: story.data,
  });
}

function editItem(_: PointerEvent, { item }: { item: ResultItem }) {
  console.log(item);
  switch (item.type) {
    case 'Initiative':
      store.commit(Mutations.ShowDrawer, {
        component: markRaw(ContentItemForm),
        data: {
          type: TYPE_INITIATIVE,
          contentItem: item.item,
        },
      });
      break;
    case 'Event':
      store.commit(Mutations.ShowDrawer, {
        component: markRaw(EventItemForm),
        data: item.item,
      });
      break;
    case 'Learning Item':
      store.commit(Mutations.ShowDrawer, {
        component: markRaw(LearningItemForm),
        data: item.item,
      });
      break;
    case 'Quiz':
      editStory(item.item as Story);
      break;
  }
}
function updateItemsPerPage(count: number) {
  filterData.value = { ...filterData.value, count };
  search();
}
function updatePage(page: number) {
  filterData.value = { ...filterData.value, page };
  search();
}

function resetPage() {
  filterData.value = { ...filterData.value, page: 1 };
}
function updateSortBy(sorting: { key: string; order: 'asc' | 'desc' }[]) {
  if (sorting.length) {
    const { key, order } = sorting[0];

    filterData.value = { ...filterData.value, sort: key, direction: order };
    resetPage();
    search();
  }
}

return (_ctx: any,_cache: any) => {
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_col = _resolveComponent("v-col")!
  const _component_v_row = _resolveComponent("v-row")!
  const _component_v_card_text = _resolveComponent("v-card-text")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_dialog = _resolveComponent("v-dialog")!
  const _component_v_chip = _resolveComponent("v-chip")!
  const _component_v_data_table_server = _resolveComponent("v-data-table-server")!
  const _component_v_container = _resolveComponent("v-container")!

  return (_openBlock(), _createBlock(_component_v_container, null, {
    default: _withCtx(() => [
      _createElementVNode("h2", null, [
        _cache[2] || (_cache[2] = _createTextVNode("Insight ")),
        _createVNode(CurrentLanguage)
      ]),
      _createVNode(_component_v_btn, {
        type: "button",
        color: "warning",
        style: {"position":"absolute","top":"70px","right":"260px","z-index":"1"},
        onClick: updateLocations
      }, {
        default: _withCtx(() => _cache[3] || (_cache[3] = [
          _createTextVNode("Update locations")
        ])),
        _: 1
      }),
      _createVNode(_component_v_dialog, { width: "500" }, {
        activator: _withCtx(({ props: activatorProps }) => [
          _createVNode(_component_v_btn, _mergeProps({
            type: "button",
            color: "warning"
          }, activatorProps, { style: {"position":"absolute","top":"70px","right":"60px","z-index":"1"} }), {
            default: _withCtx(() => _cache[4] || (_cache[4] = [
              _createTextVNode("Update categories")
            ])),
            _: 2
          }, 1040)
        ]),
        default: _withCtx(({ isActive }) => [
          _createVNode(_component_v_card, null, {
            default: _withCtx(() => [
              _createVNode(_component_v_card_text, null, {
                default: _withCtx(() => [
                  _cache[6] || (_cache[6] = _createElementVNode("h2", null, "Update categories", -1)),
                  _cache[7] || (_cache[7] = _createElementVNode("p", null, " The server will find main and sub for each sub-sub category, and re-infer main category from there. This makes searching by category more powerfull since also sub categories will now find results. ", -1)),
                  _createVNode(_component_v_row, null, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_col, null, {
                        default: _withCtx(() => [
                          _createVNode(_component_v_btn, {
                            class: "mt-2",
                            color: "warning",
                            type: "button",
                            block: "",
                            onClick: () => updateCategories(isActive)
                          }, {
                            default: _withCtx(() => _cache[5] || (_cache[5] = [
                              _createTextVNode("Update categories")
                            ])),
                            _: 2
                          }, 1032, ["onClick"])
                        ]),
                        _: 2
                      }, 1024)
                    ]),
                    _: 2
                  }, 1024)
                ]),
                _: 2
              }, 1024)
            ]),
            _: 2
          }, 1024)
        ]),
        _: 1
      }),
      _createVNode(_component_v_row, null, {
        default: _withCtx(() => [
          _createVNode(_component_v_col, null, {
            default: _withCtx(() => [
              _createVNode(CategoryField, {
                label: "Search by category",
                modelValue: category.value,
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((category).value = $event))
              }, null, 8, ["modelValue"])
            ]),
            _: 1
          }),
          _createVNode(_component_v_col, null, {
            default: _withCtx(() => [
              _createVNode(LocationField, {
                label: "Search by location",
                modelValue: location.value,
                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((location).value = $event))
              }, null, 8, ["modelValue"])
            ]),
            _: 1
          })
        ]),
        _: 1
      }),
      _createVNode(_component_v_chip, {
        density: "compact",
        color: initiativesCount.value === 0 ? 'warning' : 'info'
      }, {
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString(initiativesCount.value) + " Initiatives", 1)
        ]),
        _: 1
      }, 8, ["color"]),
      _cache[8] || (_cache[8] = _createTextVNode("  ")),
      _createVNode(_component_v_chip, {
        density: "compact",
        color: learningItemsCount.value === 0 ? 'warning' : 'info'
      }, {
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString(learningItemsCount.value) + " Learning Items", 1)
        ]),
        _: 1
      }, 8, ["color"]),
      _cache[9] || (_cache[9] = _createTextVNode("  ")),
      _createVNode(_component_v_chip, {
        density: "compact",
        color: eventItemsCount.value === 0 ? 'warning' : 'info'
      }, {
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString(eventItemsCount.value) + " Events", 1)
        ]),
        _: 1
      }, 8, ["color"]),
      _cache[10] || (_cache[10] = _createTextVNode("  ")),
      _createVNode(_component_v_chip, {
        density: "compact",
        color: storiesCount.value === 0 ? 'warning' : 'info'
      }, {
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString(storiesCount.value) + " Quizzes", 1)
        ]),
        _: 1
      }, 8, ["color"]),
      _cache[11] || (_cache[11] = _createTextVNode("  ")),
      _createVNode(_component_v_data_table_server, {
        density: "compact",
        "onClick:row": editItem,
        headers: headers,
        items: filteredContent.value,
        itemsLength: total.value,
        itemsPerPage: filterData.value.count,
        page: filterData.value.page,
        "onUpdate:itemsPerPage": updateItemsPerPage,
        "onUpdate:page": updatePage,
        "onUpdate:sortBy": updateSortBy,
        hover: ""
      }, {
        "item.category": _withCtx(({ value }) => [
          _createTextVNode(_toDisplayString(value.title), 1)
        ]),
        _: 1
      }, 8, ["items", "itemsLength", "itemsPerPage", "page"])
    ]),
    _: 1
  }))
}
}

})